<template>
  <div v-if="props.videoId" ref="playerContainer" class="video-container" :aria-label="`watch video of ${seoAlt}`">
    <video
      :id="uniqueVideoId"
      ref="player"
      class="video-js vjs-main_video"
      controls="true"
      draggable="false"
      preload="auto"
      :poster="`${videoPosterUrlTemplate}${props.videoId}-AVS`"
      @play="handleVideoPlay"
      @pause="handleVideoNotPlaying"
    >
      <track kind="captions" />
      <source :src="`${videoUrlTemplate}${props.videoId}-AVS.m3u8?packagedStreaming=true`" type="application/x-mpegURL" />
    </video>
  </div>
</template>

<script setup>
import { ref, watch, toValue, onBeforeUnmount, useId } from 'vue';
import { useProduct } from '~/modules/product/composables/useProduct';
import { useCommonProductGetters } from '~/composables/useCommonProductGetters';
import { getSeoAlt } from '~/utils/getSeoAlt';
import { loadScript } from '~/utils/loadScript';
import { loadStylesheet } from '~/utils/loadStylesheet';
import useUrlTemplate from '~/composables/useUrlTemplate';
import tagVideo from '../features/analytics/video';

const props = defineProps({
  videoId: {
    type: String,
    required: true,
  },
  isVideoSelected: {
    type: Boolean,
    required: false,
    default: false,
  },
  loadVideoPlayerOnMount: {
    type: Boolean,
    default: false,
  },
});

const { productId } = useProduct();
const { productName, brandName } = useCommonProductGetters();
const { videoUrlTemplate, videoPosterUrlTemplate } = useUrlTemplate();

const uniqueVideoId = `video_${useId()}`;

// added default duration of the video to avoid NaN in tests
const DEFAULT_DURATION = 60;
let observer = null;
const seoAlt = getSeoAlt(toValue(productName), toValue(brandName));
const player = ref({
  currentTime: 0,
  duration: DEFAULT_DURATION,
});
const playerContainer = ref(null);
const utagData = computed(() => ({
  video_id: [props.videoId],
  product_id: [productId],
  product_name: [toValue(productName)],
}));

const handleVideoPlay = () => {
  tagVideo(
    {
      videoTimeViewed: player.value.currentTime,
      videoDuration: player.value.duration || DEFAULT_DURATION,
      data: utagData.value,
    },
    'start',
  );
};
const handleVideoNotPlaying = () => {
  const { currentTime, duration } = player.value;
  const videoDuration = duration || DEFAULT_DURATION;
  if (currentTime !== videoDuration) {
    tagVideo(
      {
        videoTimeViewed: currentTime,
        videoDuration,
        data: utagData.value,
      },
      'pause',
    );
  }
};

const setupIntersectionObserver = (videojsPlayer) => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.intersectionRatio < 0.2) {
          videojsPlayer.pause();
        }
      });
    },
    {
      threshold: [0, 0.2, 1],
    },
  );

  observer.observe(videojsPlayer.el());
  return observer;
};

let videojsPlayer = null;
let isPlayerReady = false;

const loadVideoPlayer = async () => {
  if (isPlayerReady && videojsPlayer) {
    return;
  }

  await loadScript('https://cdnjs.cloudflare.com/ajax/libs/video.js/8.22.0/video.min.js');
  await loadStylesheet('https://cdnjs.cloudflare.com/ajax/libs/video.js/8.22.0/video-js.min.css');

  videojsPlayer = videojs(uniqueVideoId);
  isPlayerReady = true;
  observer = setupIntersectionObserver(videojsPlayer);

  const videoContainer = playerContainer.value.querySelectorAll('.vjs-main_video');
  if (videoContainer) {
    videoContainer.forEach((v) => v.setAttribute('draggable', 'false'));
  }
};

const updatePlayerSource = (newVideoId) => {
  if (!videojsPlayer) return;

  videojsPlayer.src({
    src: `${videoUrlTemplate}${newVideoId}-AVS.m3u8?packagedStreaming=true`,
    type: 'application/x-mpegURL',
  });

  videojsPlayer.hasStarted(false);
  videojsPlayer.poster(`${videoPosterUrlTemplate}${newVideoId}-AVS`);
};

onMounted(() => {
  if (props.loadVideoPlayerOnMount) {
    loadVideoPlayer();
  }
});

watch(
  () => props.isVideoSelected,
  (isVideoSelected) => {
    if (isVideoSelected && !props.loadVideoPlayerOnMount) {
      loadVideoPlayer();
    }
  },
);

watch(
  () => props.videoId,
  (newVideoId) => {
    if (isPlayerReady) {
      updatePlayerSource(newVideoId);
    }
  },
);

onBeforeUnmount(() => {
  videojsPlayer?.dispose();
  isPlayerReady = false;
  observer?.disconnect();
});
</script>

<style lang="scss" scoped>
.video-container {
  width: 100%;
  height: 100%;
}

.vjs-main_video {
  width: 100%;
  height: 100%;
  user-select: none;
}
</style>

<style lang="scss">
.video-js.vjs-main_video .vjs-big-play-button {
  top: 50%;
  left: 50%;
  margin-left: -1em;
  margin-top: -1em;
  width: 2em;
  height: 2em;
  line-height: 2em;
  border: none;
  border-radius: 50%;
  font-size: 5em;
  background-color: rgba(0, 0, 0, 0.45);
  color: #fff;
  -webkit-transition:
    border-color 0.4s,
    outline 0.4s,
    background-color 0.4s;
  -moz-transition:
    border-color 0.4s,
    outline 0.4s,
    background-color 0.4s;
  -ms-transition:
    border-color 0.4s,
    outline 0.4s,
    background-color 0.4s;
  -o-transition:
    border-color 0.4s,
    outline 0.4s,
    background-color 0.4s;
  transition:
    border-color 0.4s,
    outline 0.4s,
    background-color 0.4s;
}

.video-js.vjs-main_video .vjs-big-play-button:active,
.video-js.vjs-main_video .vjs-big-play-button:focus,
.video-js.vjs-main_video:active .vjs-big-play-button,
.video-js.vjs-main_video:hover .vjs-big-play-button {
  background-color: #d52484;
}

//hiding caption button as it doesn't contain captions
//track was added to pass accessibility check
.video-js.vjs-main_video .vjs-subs-caps-button {
  display: none;
}
</style>
